import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import { useGenerateBreadcrumbList } from '@/hooks/useGenerateBreadcrumbList';
import type { DocServerType } from '@/services/doc/doc';
import clsx from 'clsx';
import React from 'react';
import { BottomBanner } from '../bottom-banner/BottomBanner';
import { DocsBreadcrumb } from '../docs-breadcrumb/DocsBreadcrumb';
import { Footer } from '../footer/Footer';
import { LastModifiedAt } from '../last-modified-at/LastModifiedAt';
import { MarkdownViewer } from '../markdown-viewer/MarkdownViewer';
import { Paginator } from '../paginator/Paginator';
import { ResourceHeader } from '../resource-header/ResourceHeader';
import { SpecialFileMenu } from '../special-file-menu/SpecialFileMenu';
import { DocAnchor } from './components/doc-anchor/DocAnchor';
import styles from './DocContent.module.css';
import { useDocAnchorHeadingList } from './hooks/useDocAnchorHeadingList';

export type DocContentProps = {
  value: Pick<DocServerType, 'name' | 'content'>;
  hiddenBreadcrumb?: boolean;
  hiddenHeader?: boolean;
  hiddenAnchor?: boolean;
};

export function DocContent(props: DocContentProps) {
  const { value, hiddenHeader, hiddenBreadcrumb, hiddenAnchor } = props;

  const breadcrumbList = useGenerateBreadcrumbList();
  const navbarHeight = useCalcNavbarHeight();
  const docContentRef = React.useRef<HTMLDivElement>(null);
  const { headingList, minDepth, isHiddenAnchor } = useDocAnchorHeadingList(
    docContentRef,
    hiddenAnchor,
  );

  return (
    <ContainerQuery containerSizes={[960]} className="flex w-full @container/doc-content">
      <div
        ref={docContentRef}
        className={clsx('w-0 flex-1 os:pt-5', {
          [`${styles['container-one']}`]: navbarHeight === 56 + 1,
          [`${styles['container-two']}`]: navbarHeight === 106 + 1,
        })}
      >
        {!hiddenBreadcrumb && (
          <DocsBreadcrumb
            className="mb-2 hidden os:flex"
            value={breadcrumbList}
            rightNode={!!breadcrumbList.length && <SpecialFileMenu />}
          />
        )}
        {!hiddenHeader && (
          <ResourceHeader
            className="mb-4"
            name={value.name}
            suffix={!breadcrumbList.length && <SpecialFileMenu className="max-os:hidden" />}
          />
        )}

        <MarkdownViewer className="mb-8" value={value.content} enableHeaderPlugin inDocView />

        <BottomBanner className="mb-8" />

        <LastModifiedAt className="mb-14 mt-8" />

        <Paginator className="mb-10" />

        <Footer />
      </div>

      {!isHiddenAnchor && <DocAnchor headingList={headingList} minDepth={minDepth} />}
    </ContainerQuery>
  );
}
